import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Trash2,
  ArrowLeft,
  MessageSquare,
  User,
  Users,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import {
  useReportedPosts,
  useApprovePostReport,
  useDismissPostReport,
  useDeletePostAndWarn,
  useReportedComments,
  useDismissCommentReport,
  useDeleteCommentAndWarn,
  useReportedUsers,
  useDismissUserReport,
  useWarnUserAndResolve,
} from '../../hooks/useReportedContent';
import { useAuth } from '../../constants/providers/AuthProvider';

type ModerationTab = 'posts' | 'comments' | 'users';

function ReasonBadge({ reason }: { reason: string }) {
  const colors: Record<string, string> = {
    inappropriate: 'bg-orange-500/20 text-orange-400',
    harassment: 'bg-red-500/20 text-red-400',
    other: 'bg-gray-500/20 text-gray-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[reason] || colors.other}`}>
      {reason}
    </span>
  );
}

function formatName(user: { first_name?: string; last_name?: string } | null) {
  if (!user) return 'Unknown';
  return `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown';
}

export default function SocialModeration() {
  const [activeTab, setActiveTab] = useState<ModerationTab>('posts');
  const { user } = useAuth();

  // Post reports
  const { data: reportedPosts, isLoading: postsLoading } = useReportedPosts();
  const approvePostReport = useApprovePostReport();
  const dismissPostReport = useDismissPostReport();
  const deletePostAndWarn = useDeletePostAndWarn();

  // Comment reports
  const { data: reportedComments, isLoading: commentsLoading } = useReportedComments();
  const dismissCommentReport = useDismissCommentReport();
  const deleteCommentAndWarn = useDeleteCommentAndWarn();

  // User reports
  const { data: reportedUsers, isLoading: usersLoading } = useReportedUsers();
  const dismissUserReport = useDismissUserReport();
  const warnUser = useWarnUserAndResolve();

  const [confirmAction, setConfirmAction] = useState<{
    type: 'delete-post' | 'delete-comment' | 'warn-user';
    data: any;
  } | null>(null);

  const handleConfirm = async () => {
    if (!confirmAction || !user?.id) return;

    try {
      switch (confirmAction.type) {
        case 'delete-post':
          await deletePostAndWarn.mutateAsync({
            postId: confirmAction.data.postId,
            userId: confirmAction.data.userId,
            adminId: user.id,
            reason: confirmAction.data.reason,
            warningType: confirmAction.data.warningType,
          });
          break;
        case 'delete-comment':
          await deleteCommentAndWarn.mutateAsync({
            commentId: confirmAction.data.commentId,
            userId: confirmAction.data.userId,
            adminId: user.id,
            reason: confirmAction.data.reason,
            warningType: confirmAction.data.warningType,
            reportId: confirmAction.data.reportId,
          });
          break;
        case 'warn-user':
          await warnUser.mutateAsync({
            userId: confirmAction.data.userId,
            adminId: user.id,
            reason: confirmAction.data.reason,
            warningType: confirmAction.data.warningType || 'other',
            reportId: confirmAction.data.reportId,
          });
          break;
      }
    } catch (err) {
      console.error('Moderation action failed:', err);
    }
    setConfirmAction(null);
  };

  const tabs: { key: ModerationTab; label: string; count: number }[] = [
    { key: 'posts', label: 'Reported Posts', count: reportedPosts?.length || 0 },
    { key: 'comments', label: 'Reported Comments', count: reportedComments?.length || 0 },
    { key: 'users', label: 'Reported Users', count: reportedUsers?.length || 0 },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Link
          to="/social"
          className="p-2 rounded-lg text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#9B1421]/20 flex items-center justify-center">
            <Shield size={20} className="text-text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#EBFFEE]">Moderation</h1>
            <p className="text-sm text-[#A9A3A3]">Review reported content and manage community safety</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-[#504A4A] pb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-[#9B1421] text-[#EBFFEE]'
                : 'text-[#A9A3A3] hover:bg-[#363232] hover:text-[#EBFFEE]'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.key ? 'bg-white/20' : 'bg-[#504A4A]'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Reported Posts Tab */}
      {activeTab === 'posts' && (
        <div className="space-y-4">
          {postsLoading ? (
            <div className="text-center py-12 text-[#A9A3A3]">Loading...</div>
          ) : !reportedPosts?.length ? (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="!p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-[#EBFFEE] font-medium">All clear!</p>
                <p className="text-sm text-[#A9A3A3]">No pending post reports to review.</p>
              </CardContent>
            </Card>
          ) : (
            reportedPosts.map((report) => (
              <Card key={report.id} className="bg-[#221F1F] border-[#504A4A]">
                <CardContent className="!p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm text-[#A9A3A3]">
                        Reported by <strong className="text-[#EBFFEE]">{formatName(report.reporter)}</strong>
                      </span>
                      <ReasonBadge reason={report.reason} />
                    </div>
                    <span className="text-xs text-[#706A6A]">
                      {new Date(report.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {report.additional_info && (
                    <p className="text-xs text-[#706A6A] mb-3 italic">"{report.additional_info}"</p>
                  )}
                  <div className="p-3 rounded-lg bg-[#363232] border border-[#504A4A] mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-3 h-3 text-[#706A6A]" />
                      <span className="text-xs text-[#A9A3A3]">
                        Post by {formatName(report.post?.author)}
                      </span>
                    </div>
                    <p className="text-sm text-[#EBFFEE] line-clamp-3">{report.post?.content}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => dismissPostReport.mutate({ reportId: report.id })}
                      className="bg-[#363232] hover:bg-[#504A4A] text-[#A9A3A3] px-3 py-1.5 text-xs rounded-lg"
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                    <Button
                      onClick={() => approvePostReport.mutate({ reportId: report.id })}
                      className="bg-green-600/20 hover:bg-green-600/30 text-green-400 px-3 py-1.5 text-xs rounded-lg"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Resolve
                    </Button>
                    <Button
                      onClick={() =>
                        setConfirmAction({
                          type: 'delete-post',
                          data: {
                            postId: report.post_id,
                            userId: report.post?.user_id,
                            reason: report.reason,
                            warningType: report.reason,
                          },
                        })
                      }
                      className="bg-red-600/20 hover:bg-red-600/30 text-red-400 px-3 py-1.5 text-xs rounded-lg"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete & Warn
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Reported Comments Tab */}
      {activeTab === 'comments' && (
        <div className="space-y-4">
          {commentsLoading ? (
            <div className="text-center py-12 text-[#A9A3A3]">Loading...</div>
          ) : !reportedComments?.length ? (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="!p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-[#EBFFEE] font-medium">All clear!</p>
                <p className="text-sm text-[#A9A3A3]">No pending comment reports.</p>
              </CardContent>
            </Card>
          ) : (
            reportedComments.map((report) => (
              <Card key={report.id} className="bg-[#221F1F] border-[#504A4A]">
                <CardContent className="!p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm text-[#A9A3A3]">
                        Comment reported by <strong className="text-[#EBFFEE]">{formatName(report.reporter)}</strong>
                      </span>
                      <ReasonBadge reason={report.reason} />
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-[#363232] border border-[#504A4A] mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-3 h-3 text-[#706A6A]" />
                      <span className="text-xs text-[#A9A3A3]">
                        Comment by {formatName(report.comment?.user)}
                      </span>
                    </div>
                    <p className="text-sm text-[#EBFFEE]">{report.comment?.content}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => dismissCommentReport.mutate({ reportId: report.id })}
                      className="bg-[#363232] hover:bg-[#504A4A] text-[#A9A3A3] px-3 py-1.5 text-xs rounded-lg"
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                    <Button
                      onClick={() =>
                        setConfirmAction({
                          type: 'delete-comment',
                          data: {
                            commentId: report.comment_id,
                            userId: report.comment?.user_id,
                            reason: report.reason,
                            warningType: report.reason,
                            reportId: report.id,
                          },
                        })
                      }
                      className="bg-red-600/20 hover:bg-red-600/30 text-red-400 px-3 py-1.5 text-xs rounded-lg"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete & Warn
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Reported Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {usersLoading ? (
            <div className="text-center py-12 text-[#A9A3A3]">Loading...</div>
          ) : !reportedUsers?.length ? (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardContent className="!p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-[#EBFFEE] font-medium">All clear!</p>
                <p className="text-sm text-[#A9A3A3]">No pending user reports.</p>
              </CardContent>
            </Card>
          ) : (
            reportedUsers.map((report) => (
              <Card key={report.id} className="bg-[#221F1F] border-[#504A4A]">
                <CardContent className="!p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm text-[#A9A3A3]">
                        <strong className="text-[#EBFFEE]">{formatName(report.reported_user)}</strong>
                        {' '}reported by {formatName(report.reporter)}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-[#A9A3A3] mb-3">Reason: {report.reason}</p>
                  {report.additional_info && (
                    <p className="text-xs text-[#706A6A] mb-3 italic">"{report.additional_info}"</p>
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => dismissUserReport.mutate({ reportId: report.id })}
                      className="bg-[#363232] hover:bg-[#504A4A] text-[#A9A3A3] px-3 py-1.5 text-xs rounded-lg"
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                    <Button
                      onClick={() =>
                        setConfirmAction({
                          type: 'warn-user',
                          data: {
                            userId: report.reported_user_id,
                            reason: report.reason,
                            warningType: 'other',
                            reportId: report.id,
                          },
                        })
                      }
                      className="bg-red-600/20 hover:bg-red-600/30 text-red-400 px-3 py-1.5 text-xs rounded-lg"
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" /> Warn User
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!confirmAction}
        onCancel={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        title={
          confirmAction?.type === 'delete-post'
            ? 'Delete Post & Warn User'
            : confirmAction?.type === 'delete-comment'
              ? 'Delete Comment & Warn User'
              : 'Warn User'
        }
        message="This will apply a progressive warning to the user. Repeated warnings may restrict their posting and commenting permissions."
        confirmText={confirmAction?.type === 'warn-user' ? 'Warn User' : 'Delete & Warn'}
      />
    </div>
  );
}
